import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Play, Plus, Minus } from 'lucide-react';
import Header from '../components/Header';
import SEOHead, { breadcrumbSchema, faqSchema, seoServiceSchema } from '../components/SEOHead';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Reveal';
import { Magnetic } from '../components/motion/Magnetic';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FaqItem = ({ faq, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border-t border-black/10" data-testid={`seo-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-6 py-6 text-left"
      >
        <span className="font-presti text-xl md:text-2xl text-black/90 leading-snug">{faq.question}</span>
        <span className="mt-1 text-black/40 shrink-0">{open ? <Minus size={18} /> : <Plus size={18} />}</span>
      </button>
      <div className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <p className="overflow-hidden text-black/60 leading-relaxed pb-6 pr-10">{faq.answer}</p>
      </div>
    </div>
  );
};

const NotFound = () => (
  <main className="bg-white min-h-screen flex items-center justify-center px-6 text-center" data-testid="seo-page-not-found">
    <SEOHead title="Page Not Found" noindex />
    <div>
      <p className="text-[10px] tracking-[0.4em] uppercase text-black/30 mb-6">404</p>
      <h1 className="font-presti text-4xl sm:text-5xl text-black mb-8">This page doesn&rsquo;t exist</h1>
      <Link to="/" className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-black/60 hover:text-black border-b border-black/20 pb-1">
        Back home <ArrowRight size={14} />
      </Link>
    </div>
  </main>
);

export default function SeoLandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState({});
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    setStatus('loading');
    Promise.all([
      fetch(`${API_URL}/api/seo-pages/${slug}`),
      fetch(`${API_URL}/api/content/settings`).then((r) => r.json()).catch(() => ({})),
    ]).then(async ([res, s]) => {
      setSettings(s || {});
      if (!res.ok) { setStatus('missing'); return; }
      setPage(await res.json());
      setStatus('ready');
    }).catch(() => setStatus('missing'));
  }, [slug]);

  if (status === 'loading') {
    return <main className="bg-white min-h-screen flex items-center justify-center"><p className="text-xs tracking-[0.3em] uppercase text-black/30">Loading…</p></main>;
  }
  if (status === 'missing') return <NotFound />;

  const logoUrl = settings.photo_logo_url || settings.logo_url || '';
  const cols = page.collections || [];
  const films = (page.films || []).filter((f) => f.thumbnail_url || f.video_url || f.video_url_low);

  return (
    <main className="bg-white min-h-screen text-black" data-testid="seo-landing-page">
      <SEOHead
        title={page.title.replace(/\s*\|\s*Crystal Media$/i, '')}
        description={page.meta_description}
        url={`/${page.slug}`}
        keywords={page.keywords || []}
        image={page.hero_image_url || cols[0]?.cover_image_url}
        siteSettings={settings}
        jsonLd={[
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: page.h1, path: `/${page.slug}` }]),
          seoServiceSchema(page),
          ...(page.faqs?.length ? [faqSchema(page.faqs)] : []),
        ]}
      />
      <Header logoUrl={logoUrl} variant="light" />

      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-16 md:pb-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <Reveal><p className="text-[10px] tracking-[0.4em] uppercase text-black/40 mb-6" data-testid="seo-eyebrow">{page.eyebrow}</p></Reveal>
            <Reveal delay={0.08}>
              <h1 className="font-presti text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-black" data-testid="seo-h1">{page.h1}</h1>
            </Reveal>
          </div>
          <Reveal delay={0.16} className="col-span-12 lg:col-span-7 lg:col-start-4 mt-4">
            <p className="text-black/60 text-base md:text-lg leading-relaxed" data-testid="seo-intro">{page.intro}</p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Magnetic>
                <Link to="/inquire" className="inline-flex items-center gap-3 px-9 py-4 bg-black text-white text-xs tracking-[0.25em] uppercase hover:bg-black/85 transition-colors" data-testid="seo-cta-primary">
                  {page.cta_text || 'Check Your Date'} <ArrowRight size={14} />
                </Link>
              </Magnetic>
              <Link to={page.service === 'videographer' ? '/film' : '/photo'} className="inline-flex items-center justify-center gap-3 px-9 py-4 border border-black/20 text-black/70 text-xs tracking-[0.25em] uppercase hover:bg-black hover:text-white hover:border-black transition-colors" data-testid="seo-cta-secondary">
                {page.service === 'videographer' ? 'Watch Films' : 'View Gallery'}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Featured collections */}
      {cols.length > 0 && (
        <section className="px-6 md:px-12 pb-20 md:pb-28" data-testid="seo-collections">
          <div className="max-w-6xl mx-auto">
            <Reveal className="flex items-end justify-between mb-10">
              <h2 className="font-presti text-2xl md:text-3xl text-black/90">Recent {page.culture && page.culture !== 'Destination' ? page.culture + ' ' : ''}weddings</h2>
              <Link to="/collections" className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-black/50 hover:text-black">All collections <ArrowRight size={12} /></Link>
            </Reveal>
            <Stagger className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {cols.map((c) => (
                <StaggerItem key={c.id}>
                  <Link to={`/collection/${c.id}`} className="group block" data-testid={`seo-collection-${c.id}`}>
                    <div className="aspect-[3/4] overflow-hidden bg-[#f0f0f0]">
                      <img
                        src={c.cover_image_url}
                        alt={`${c.couple} — ${c.location} wedding photography by Crystal Media`}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                        style={{ objectPosition: `${c.cover_focal_point?.x ?? 50}% ${c.cover_focal_point?.y ?? 50}%` }}
                      />
                    </div>
                    <p className="font-presti text-lg md:text-xl mt-4 text-black/85">{c.couple}</p>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-black/40">{c.location}</p>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* Films */}
      {films.length > 0 && (
        <section className="bg-black text-white px-6 md:px-12 py-20 md:py-28" data-testid="seo-films">
          <div className="max-w-6xl mx-auto">
            <Reveal className="flex items-end justify-between mb-10">
              <h2 className="font-presti text-2xl md:text-3xl">Films</h2>
              <Link to="/film/theatre" className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-white/50 hover:text-white">Enter theatre <ArrowRight size={12} /></Link>
            </Reveal>
            <Stagger className={`grid gap-6 ${films.length === 1 ? 'grid-cols-1 max-w-3xl' : 'grid-cols-1 md:grid-cols-2'}`}>
              {films.map((f) => (
                <StaggerItem key={f.id}>
                  <Link to="/film/theatre" className="group relative block aspect-video overflow-hidden border border-white/10">
                    {f.thumbnail_url ? (
                      <img src={f.thumbnail_url} alt={`${f.title} wedding film`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" />
                    ) : (
                      <video src={f.video_url_low || f.video_url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                    )}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors duration-500 flex items-center justify-center">
                      <span className="w-14 h-14 flex items-center justify-center border border-white/50 bg-white/10 backdrop-blur-md"><Play size={18} fill="white" className="ml-0.5" /></span>
                    </div>
                    <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="font-presti text-xl">{f.title}</p>
                      {f.subtitle && <p className="text-white/50 text-xs mt-1">{f.subtitle}</p>}
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* Body sections */}
      {page.sections?.length > 0 && (
        <section className="px-6 md:px-12 py-20 md:py-28" data-testid="seo-sections">
          <div className="max-w-6xl mx-auto divide-y divide-black/10">
            {page.sections.map((s, i) => (
              <Reveal key={i} className="grid grid-cols-12 gap-6 py-12 md:py-16">
                <h2 className="col-span-12 md:col-span-4 font-presti text-2xl md:text-3xl text-black/90">{s.heading}</h2>
                <p className="col-span-12 md:col-span-7 md:col-start-6 text-black/60 leading-relaxed text-base md:text-lg">{s.content}</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {page.faqs?.length > 0 && (
        <section className="bg-[#fafafa] px-6 md:px-12 py-20 md:py-28" data-testid="seo-faqs">
          <div className="max-w-6xl mx-auto grid grid-cols-12 gap-8">
            <Reveal className="col-span-12 md:col-span-4">
              <p className="text-[10px] tracking-[0.4em] uppercase text-black/40 mb-4">Questions</p>
              <h2 className="font-presti text-3xl md:text-4xl text-black">Good to know</h2>
            </Reveal>
            <Reveal delay={0.1} className="col-span-12 md:col-span-8 border-b border-black/10">
              {page.faqs.map((f, i) => <FaqItem key={i} faq={f} index={i} />)}
            </Reveal>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-black text-white py-24 md:py-32 px-6 text-center">
        <Reveal className="max-w-2xl mx-auto">
          <h2 className="font-presti text-3xl sm:text-4xl md:text-5xl leading-[1.1] mb-5">Tell us about your wedding</h2>
          <p className="text-white/50 text-sm md:text-base mb-10">We reply within 24 hours with availability and a tailored proposal.</p>
          <Magnetic>
            <Link to="/inquire" className="inline-flex items-center gap-3 px-10 py-4 bg-white text-black text-xs tracking-[0.25em] uppercase hover:bg-white/90 transition-colors" data-testid="seo-cta-final">
              {page.cta_text || 'Check Your Date'} <ArrowRight size={14} />
            </Link>
          </Magnetic>
        </Reveal>
      </section>

      {/* Related pages — internal linking */}
      {page.related?.length > 0 && (
        <nav className="px-6 md:px-12 py-14 border-t border-black/5" aria-label="Related services" data-testid="seo-related">
          <div className="max-w-6xl mx-auto">
            <p className="text-[10px] tracking-[0.4em] uppercase text-black/30 mb-6">Also explore</p>
            <ul className="flex flex-wrap gap-x-8 gap-y-3">
              {page.related.map((r) => (
                <li key={r.slug}><Link to={`/${r.slug}`} className="text-sm text-black/55 hover:text-black transition-colors">{r.h1}</Link></li>
              ))}
              <li><Link to="/reviews" className="text-sm text-black/55 hover:text-black transition-colors">Reviews</Link></li>
              <li><Link to="/services" className="text-sm text-black/55 hover:text-black transition-colors">All services</Link></li>
            </ul>
          </div>
        </nav>
      )}

      <footer className="py-10 px-6 border-t border-black/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-black/30">© {new Date().getFullYear()} Crystal Media · Toronto, Canada</p>
          <div className="flex items-center gap-6">
            {[['/photo', 'Photo'], ['/film', 'Film'], ['/about', 'About'], ['/inquire', 'Inquire']].map(([p, l]) => (
              <Link key={p} to={p} className="text-[10px] tracking-[0.2em] uppercase text-black/40 hover:text-black transition-colors">{l}</Link>
            ))}
          </div>
        </div>
      </footer>
    </main>
  );
}
